/* eslint-disable no-unused-vars */
import jwtDecode from "jwt-decode";
import logoutAndClearData from "../../../utils/utils";

import {
  CLEAR_AUTH_DATA,
  LOAD_AUTH_DATA,
  SET_AUTH_TOKEN,
  SET_KEYCLOAK_INSTANCE,
  SET_LOADING,
  SET_RECENT_API_CALLS,
  SET_REFRESH,
  SET_REMAINING_TIME,
} from "./actionTypes";
import {
  API_RESOURCE_URLS,
  MESSAGE,
  USER_OPERATIONS,
  USER_TYPE,
} from "../../../constants";
import { API } from "../../../apis/api";
import StorageService from "../../../utils/storageService";

/*
 * load user profile from required backend
 * eg (from production-planning):
 * ```
 * {
 *  const response = await API.get(API_RESOURCE_URLS.USER_PROFILE);
 *  return response.data;
 * }
 * ```
 */
export const getUserProfile = async () => ({});

/*
 * load user auth policies from required backend for specified resources and type of operations
 * eg (from production-planning):
 * ```
 * {
 *  try {
 *  const response = await API.get(API_RESOURCE_URLS.AUTH_BULK_RESOURCE, {
 *    params: {
 *       resourceGroup: 'production-planning',
 *       operation: Object.values(USER_OPERATIONS),
 *     },
 *   });
 *   return response.data;
 * } catch (error) {
 *   console.log('failed to load auth policy', error);
 *   return [];
 *  }
 * }
 * ```
 */
const loadAuthPolicies = async (resourceGroup) => {
  try {
    const response = await API.get(API_RESOURCE_URLS.AUTH_BULK_RESOURCE, {
      params: {
        resourceGroup,
        operation: Object.values(USER_OPERATIONS),
      },
    });
    return response.data;
  } catch (error) {
    console.log("failed to load resources, error:", error);
    return null;
  }
};

const getUserRoles = (resourceAccess) => {
  try {
    const clientId = StorageService.getItem("clientId");

    if (!clientId) {
      console.warn("clientId not found in localStorage");
      return [];
    }

    const clientRoles = resourceAccess[clientId]?.roles || [];

    return clientRoles;
  } catch (error) {
    console.error("Failed to extract roles from token", error);
    return [];
  }
};

export const logoutUser = () => async (dispatch, getState) => {
  const { keycloak } = getState().auth;

  const success = await logoutAndClearData(keycloak);

  if (success) {
    dispatch({ type: CLEAR_AUTH_DATA });
    window.location.href = "/";
  } else {
    console.warn("Logout failed. Not redirecting.");
  }
};


const setKeycloakInstance = (keycloak) => ({
  type: SET_KEYCLOAK_INSTANCE,
  instance: keycloak,
});

export const setLoading = (loading) => ({
  type: SET_LOADING,
  loading,
});

const generateUser = async (token) => {
  try {
    const {
      name,
      email,
      sub: userId,
      plants,
      flowhouse_functions: flowhouseFunctions,
      flowhouse_warehouses: flowhouseWarehouses,
      resource_access: resourceAccess,
    } = jwtDecode(token);

    const roles = getUserRoles(resourceAccess);
    const userProfile = await getUserProfile();

    const user = {
      name,
      email,
      userId,
      ...userProfile,
      plants,
      flowhouseWarehouses,
      flowhouseFunctions,
      isLoaded: true,
      roles: roles || [],
      isRTA: roles?.includes(USER_TYPE.RTA) || false,
      isApprover: roles?.includes(USER_TYPE.APPROVER) || false,
      isPerformer: roles?.includes(USER_TYPE.PERFORMER) || false,
      isAdmin: roles?.includes(USER_TYPE.ADMIN) || false,
      isAuditor: roles?.includes(USER_TYPE.AUDITOR) || false,
    };

    // Add a flag to indicate if user has no roles
    user.hasNoRoles = !roles || roles.length === 0;

    return user;
  } catch (err) {
    console.log("failed to load user profile, err:", err);
    return null;
  }
};

const loadAuthData = (token, authenticated, showError) => async (dispatch) => {
  try {
    const user = await generateUser(token);
    localStorage.setItem("access_token", token);

    if (user?.hasNoRoles) {
      showError({
        contextText:
          "Access Denied: You don't have the required permissions to use this application. Please contact your system administrator.",
        onClose: () => {
          dispatch(logoutUser());
        },
      });
      return;
    }

    dispatch(setLoading(false));
    dispatch({
      type: LOAD_AUTH_DATA,
      payload: {
        user,
        isAuthenticated: authenticated,
      },
    });
  } catch (error) {
    showError({
      contextText: MESSAGE.PERMISSION_DENIED,
    });
  }
};

export const setAuthToken = (token) => ({
  type: SET_AUTH_TOKEN,
  token,
});

export const setRecentAPICalls = (recentAPICalls) => async (dispatch) => {
  dispatch({
    type: SET_RECENT_API_CALLS,
    payload: recentAPICalls,
  });
};

export const setRemainingTime = (remainingTime) => async (dispatch) => {
  dispatch({
    type: SET_REMAINING_TIME,
    payload: remainingTime,
  });
};

const setRefresh = (refresh) => async (dispatch) => {
  dispatch({
    type: SET_REFRESH,
    payload: refresh,
  });
};

export default {
  loadAuthData,
  setKeycloakInstance,
  logoutUser,
  setAuthToken,
  setRemainingTime,
  setRecentAPICalls,
  setRefresh,
  setLoading,
};
